import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getCategoryIcon } from '../utils/categoryUtils';
import { fetchTopCategories } from '../services/apiClient';

const FooterDropdown = ({ title, children }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-slate-800 sm:border-0">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between py-3 text-left sm:hidden"
      >
        <h4 className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">{title}</h4>
        <svg
          className={`h-4 w-4 text-slate-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <h4 className="hidden text-xs font-semibold uppercase tracking-[0.3em] text-slate-400 sm:block sm:text-sm">{title}</h4>
      <div className={`overflow-hidden transition-all duration-300 sm:mt-3 sm:block ${isOpen ? 'max-h-96 pb-3' : 'max-h-0 sm:max-h-none'}`}>
        {children}
      </div>
    </div>
  );
};

const Footer = () => {
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const result = await fetchTopCategories(6);
        const apiCategories = (result.data || []).map((cat) => ({
          name: cat.name,
          slug: cat.slug,
          icon: getCategoryIcon(cat.name),
        }));

        setCategories(apiCategories);
      } catch {
        setCategories([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadCategories();
  }, []);

  const year = new Date().getFullYear();

  return (
    <footer className="bg-slate-950 text-slate-200" style={{ minHeight: '400px' }}>
      <div className="mx-auto max-w-7xl px-3 py-8 sm:px-6 sm:py-10">
        <div className="flex flex-col gap-4 border-b border-slate-800 pb-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-white sm:text-xl">Stay in the PillPoint loop</h3>
            <p className="mt-1 text-xs text-slate-400 sm:text-sm">Monthly offers and new launches — no spam, ever.</p>
          </div>
          <form
            className="flex w-full max-w-md gap-2"
            onSubmit={(event) => {
              event.preventDefault();
            }}
          >
            <input
              type="email"
              placeholder="Email for health updates"
              className="flex-1 rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-400/40"
            />
            <button
              type="submit"
              className="rounded-xl bg-teal-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-teal-500"
            >
              Subscribe
            </button>
          </form>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-0 sm:grid-cols-2 sm:gap-6 lg:grid-cols-5">
          <div className="flex flex-col gap-3 border-b border-slate-800 pb-4 sm:border-0 sm:pb-0">
            <Link to="/" onClick={() => window.scrollTo(0, 0)} className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-teal-600 text-lg font-semibold text-white">
                P
              </span>
              <span className="text-lg font-bold text-white">
                Pill<span className="text-teal-400">Point</span>
              </span>
            </Link>
            <p className="text-xs leading-relaxed text-slate-400 sm:text-sm">
              Trusted UK-based pharmacy delivering licensed medicines with expert support and discreet packaging.
            </p>
          </div>

          <FooterDropdown title="Company">
            <ul className="space-y-2 text-xs text-slate-400 sm:text-sm">
              {[{ name: 'Home', path: '/' }, { name: 'All Medicines', path: '/products' }, { name: 'Blog', path: '/blog' }, { name: 'About', path: '/about' }, { name: 'Contact', path: '/contact' }, { name: 'Track Order', path: '/track-order' }].map((link) => (
                <li key={link.name}>
                  <Link to={link.path} onClick={() => window.scrollTo(0, 0)} className="transition hover:text-white">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </FooterDropdown>

          <FooterDropdown title="Popular categories">
            <ul className="space-y-2 text-xs text-slate-400 sm:text-sm">
              {isLoading ? (
                // Skeleton loader with fixed height to prevent layout shift
                <>
                  {[...Array(6)].map((_, i) => (
                    <li key={i} className="h-5 animate-pulse bg-slate-800 rounded w-3/4"></li>
                  ))}
                </>
              ) : (
                <>
                  {categories.map((category) => (
                    <li key={category.name}>
                      <Link
                        to={`/category/${encodeURIComponent(category.slug)}`}
                        onClick={() => window.scrollTo(0, 0)}
                        className="flex items-center gap-2 transition hover:text-white"
                      >
                        <span>{category.icon}</span>
                        <span>{category.name}</span>
                      </Link>
                    </li>
                  ))}
                </>
              )}
              <li>
                <Link to="/categories" onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center gap-1 font-semibold text-teal-400 transition hover:text-white">
                  View all categories
                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </li>
            </ul>
          </FooterDropdown>

          <FooterDropdown title="Our Policies">
            <ul className="space-y-2 text-xs text-slate-400 sm:text-sm">
              <li>
                <Link to="/privacy" onClick={() => window.scrollTo(0, 0)} className="transition hover:text-white">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" onClick={() => window.scrollTo(0, 0)} className="transition hover:text-white">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/shipping" onClick={() => window.scrollTo(0, 0)} className="transition hover:text-white">
                  Shipping Policy
                </Link>
              </li>
              <li>
                <Link to="/refund" onClick={() => window.scrollTo(0, 0)} className="transition hover:text-white">
                  Refund Policy
                </Link>
              </li>
              <li>
                <Link to="/drug-policy" onClick={() => window.scrollTo(0, 0)} className="transition hover:text-white">
                  Drug Policy
                </Link>
              </li>
              <li>
                <Link to="/cancellation-policy" onClick={() => window.scrollTo(0, 0)} className="transition hover:text-white">
                  Cancellation Policy
                </Link>
              </li>
              <li>
                <Link to="/communication-policy" onClick={() => window.scrollTo(0, 0)} className="transition hover:text-white">
                  Communication Policy
                </Link>
              </li>
            </ul>
          </FooterDropdown>

          <FooterDropdown title="Contact">
            <ul className="space-y-2 text-xs text-slate-400 sm:text-sm">
              <li>
                <a href="tel:+447877617712" className="inline-flex items-center gap-2 transition hover:text-white">
                  <span>📞</span>
                  +44 7877617712
                </a>
              </li>
              <li>
                <a href="mailto:pillspointuk@gmail.com" className="inline-flex items-center gap-2 transition hover:text-white">
                  <span>📧</span>
                  pillspointuk@gmail.com
                </a>
              </li>
              <li className="inline-flex items-center gap-2">
                <span>📍</span>
                London, United Kingdom
              </li>
            </ul>
            <div className="mt-4 inline-flex items-center gap-3 rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-slate-400">
              <span>🔒 Secure</span>
              <span>💳 Encrypted</span>
              <span>🛡️ Protected</span>
            </div>
          </FooterDropdown>
        </div>
      </div>

      {/* Bottom bar - separate colored section */}
      <div className="bg-slate-900 border-t border-slate-800">
        <div className="mx-auto max-w-7xl px-3 py-3 sm:px-6">
          <div className="text-center text-[11px] text-slate-500 sm:text-xs">
            <span>© {year} PillPoint UK. All rights reserved.</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
